/**
 * Step 1 Mobile: Plan Basics
 * Single-column stacked layout with segmented control for plan type.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { Step1FormData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';

const Step1Schema = z.object({
  planName: z.string().min(1, 'Plan name is required').max(100),
  planType: z.enum(['SINGLE', 'JOINT'], { required_error: 'Please select a plan type' }),
  planAsOfDate: z.string().min(1, 'Plan date is required'),
});

interface Step1Props {
  initialData?: Partial<Step1FormData>;
  onNext: (data: Step1FormData) => void;
  onSave: (data: Step1FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

export default function Step1PlanBasicsMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step1Props>) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step1FormData>({
    resolver: zodResolver(Step1Schema),
    defaultValues: {
      planName: initialData?.planName || '',
      planType: initialData?.planType || 'SINGLE',
      planAsOfDate: initialData?.planAsOfDate || new Date().toISOString().split('T')[0],
    },
  });

  const handleBlur = () => {
    onSave(watch() as Step1FormData);
  };

  return (
    <WizardMobileShell
      step={1}
      totalSteps={10}
      title="Plan Basics"
      onBack={onBack}
      onContinue={() => handleSubmit(onNext)()}
      lastSaved={lastSaved}
    >
      <div className="mobile-step-form">
        <label htmlFor="mobile-planName" className="mobile-label">
          Plan Name <span className="required">*</span>
        </label>
        <input
          id="mobile-planName"
          type="text"
          className={`mobile-input${errors.planName ? ' mobile-input--error' : ''}`}
          placeholder="e.g., Our Retirement Plan"
          {...register('planName')}
          onBlur={handleBlur}
        />
        {errors.planName && <span className="mobile-error">{errors.planName.message}</span>}

        <fieldset className="mobile-fieldset">
          <legend className="mobile-label">
            Plan Type <span className="required">*</span>
          </legend>
          <div className="mobile-segmented-control">
            <label className={`mobile-segment${watch('planType') === 'SINGLE' ? ' mobile-segment--active' : ''}`}>
              <input type="radio" value="SINGLE" className="mobile-segment-input" {...register('planType')} onBlur={handleBlur} />
              <span>👤 Single</span>
            </label>
            <label className={`mobile-segment${watch('planType') === 'JOINT' ? ' mobile-segment--active' : ''}`}>
              <input type="radio" value="JOINT" className="mobile-segment-input" {...register('planType')} onBlur={handleBlur} />
              <span>👥 Joint</span>
            </label>
          </div>
        </fieldset>

        <label htmlFor="mobile-planDate" className="mobile-label">
          Date of your balances <span className="required">*</span>
        </label>
        <input
          id="mobile-planDate"
          type="date"
          className={`mobile-input${errors.planAsOfDate ? ' mobile-input--error' : ''}`}
          {...register('planAsOfDate')}
          onBlur={handleBlur}
        />
        <span className="mobile-hint">Usually today. The date your balances are entered as of.</span>
      </div>
    </WizardMobileShell>
  );
}
